import { ReactElement } from 'react'
import { ProgressBar, ProgressTone } from '../ProgressBar'
import * as S from './styles'

export type CapacityBandState = S.BandState

export interface CapacityBandData {
  state: CapacityBandState
  percentage: number | null
  percentText: string | null
  issued: number
  capacity: number | null
  stock: number | null
  sectors: { nearlySoldOut: number, soldOut: number } | null
}

const TONES: Record<Exclude<CapacityBandState, 'sem'>, ProgressTone> = { ok: 'positive', quase: 'warning', esgotado: 'danger' }
const BADGES: Partial<Record<CapacityBandState, string>> = { quase: 'Quase esgotado', esgotado: 'Esgotado' }

const count = (value: number): string => value.toLocaleString('pt-BR')
const plural = (value: number, one: string, many: string): string => (value === 1 ? one : many)

export function CapacityBand({ band }: { band: CapacityBandData }): ReactElement {
  const badge = BADGES[band.state]
  return (
    <S.Band aria-label="Capacidade vendida">
      <S.Label>Capacidade vendida</S.Label>
      {band.state === 'sem' || band.percentage === null ? (
        <S.None>sem capacidade definida</S.None>
      ) : (
        <>
          <S.Headline>
            <S.Percent $state={band.state}>{band.percentText}</S.Percent>
            {badge && <S.Badge $state={band.state === 'esgotado' ? 'esgotado' : 'quase'}>{badge}</S.Badge>}
          </S.Headline>
          <ProgressBar
            percentage={band.percentage}
            label="Capacidade vendida do evento"
            valueText={`${band.percentText ?? ''} da capacidade vendida do evento`}
            track="neutral"
            tone={TONES[band.state]}
            height={10}
          />
        </>
      )}
      <S.Line>
        <span>
          {`${count(band.issued)} emitidos`}
          {band.capacity !== null && ` de ${count(band.capacity)} de capacidade`}
        </span>
        {band.stock !== null && <span>{`${count(band.stock)} em estoque`}</span>}
      </S.Line>
      {band.sectors && (
        <S.Counters>
          <S.Counter $state="quase">
            <b>{count(band.sectors.nearlySoldOut)}</b>{' '}
            {plural(band.sectors.nearlySoldOut, 'setor acima de 90%', 'setores acima de 90%')}
          </S.Counter>
          <S.Counter $state="esgotado">
            <b>{count(band.sectors.soldOut)}</b> {plural(band.sectors.soldOut, 'setor esgotado', 'setores esgotados')}
          </S.Counter>
        </S.Counters>
      )}
    </S.Band>
  )
}
